import i18next from 'i18next'
import Backend from 'i18next-http-backend'
import { initReactI18next } from 'react-i18next'
import { enviroment } from './enviroment'
import { buildLocalesLoadPath } from './utils/functions/localesLoadPath'

export type AppLanguage = 'en' | 'da' | 'nl' | 'de' | 'sv' | 'no' | 'es' | 'it' | 'fr' | 'pl'

export const SUPPORTED_LANGUAGES: ReadonlyArray<{ code: AppLanguage, label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'da', label: 'Dansk' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'no', label: 'Norsk' },
  { code: 'pl', label: 'Polski' },
  { code: 'sv', label: 'Svenska' }
]

const SUPPORTED_LANGUAGE_CODES: ReadonlyArray<AppLanguage> = SUPPORTED_LANGUAGES.map(l => l.code)

export function isAppLanguage (value: string | null | undefined): value is AppLanguage {
  return value != null && (SUPPORTED_LANGUAGE_CODES as ReadonlyArray<string>).includes(value)
}

// True when the current UI language is Danish. Matches both the plain 'da'
// code we use internally and regional variants like 'da-DK' for robustness.
// Used to gate Denmark-only UI such as the chain-logos (Kædelogoer) section.
export function isDanishLanguage (language: string | null | undefined): boolean {
  return language === 'da' || (language?.startsWith('da') ?? false)
}

const LANGUAGE_STORAGE_KEY = 'kitmaker.lang'

function getStoredLanguage (): AppLanguage {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
    if (isAppLanguage(stored)) {
      return stored
    }
  } catch {
    // localStorage unavailable — fall back to default
  }
  return 'en'
}

export function setLanguage (language: AppLanguage) {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch {
    // localStorage unavailable — language still changes for this session
  }
  i18next.changeLanguage(language)
}

i18next.use(Backend).use(initReactI18next).init({
  debug: false,
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_LANGUAGE_CODES as string[],
  lng: getStoredLanguage(),
  // Language files are fetched from '/locales/{{lng}}/{{ns}}.json' (the
  // backend default) - with '?v=<build id>' appended when the build carries
  // REACT_APP_BUILD_ID (CI: the commit sha), so every deploy fetches its own
  // translation.json instead of a copy a browser or proxy cached from the
  // previous deploy (new keys showed up as English on 11/9). Unset = the
  // default path, i.e. exactly today's request.
  backend: { loadPath: buildLocalesLoadPath(enviroment.buildId) },
  interpolation: {
    // Standard for React: React escapes rendered strings itself, so i18next's
    // own HTML-escaping double-escaped interpolated values — e.g. an org name
    // like "Jet-Sport A/S" rendered literally as "A&#x2F;S" in the branding
    // banner. Per-call `interpolation: { escapeValue: false }` options that
    // predate this global default are harmless no-ops and may remain.
    escapeValue: false
  }
})

export default i18next
